import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useScopedOrders } from '../../hooks/useScopedOrders';
import {
  filterOpsServicesByDate,
  formatOpsServiceDate,
  getOpsServiceStatusColor,
  getOpsServiceStatusLabel,
  getOpsServiceTypeLabel,
  parseOpsServicesFromMeta,
  shiftInputDate,
  toInputDateValue,
  type OpsService,
  type OpsServiceType,
} from '../../utils/opsServices';
import type { OrderNode } from '../../graphql/queries/orders';

type DailyTab = 'all' | OpsServiceType;

interface DailyServiceRow {
  service: OpsService;
  order: OrderNode;
  orderNo: string;
  customerName: string;
}

function getCustomerName(order: OrderNode): string {
  const billing = order.billing;
  if (billing?.firstName || billing?.lastName) {
    return [billing.firstName, billing.lastName].filter(Boolean).join(' ');
  }
  return order.customer?.email || billing?.email || '—';
}

const DailyOperations: React.FC = () => {
  document.title = 'Daily Operations | NGK Tours';
  const [selectedDate, setSelectedDate] = useState(toInputDateValue());
  const [activeTab, setActiveTab] = useState<DailyTab>('all');
  const [showCancelled, setShowCancelled] = useState(false);

  const { orders, loading, error, refetch } = useScopedOrders({ first: 100, paginateAll: true });

  const rows = useMemo(() => {
    const list: DailyServiceRow[] = [];
    orders.forEach((order) => {
      const services = filterOpsServicesByDate(
        parseOpsServicesFromMeta(order.metaData),
        selectedDate,
        { includeCancelled: showCancelled }
      );
      services.forEach((service) => {
        list.push({
          service,
          order,
          orderNo: `#${order.orderNumber ?? order.databaseId}`,
          customerName: getCustomerName(order),
        });
      });
    });
    return list.sort((a, b) =>
      (
        a.service.pickupTime ||
        a.service.serviceTime ||
        a.service.flightTime ||
        ''
      ).localeCompare(
        b.service.pickupTime || b.service.serviceTime || b.service.flightTime || ''
      )
    );
  }, [orders, selectedDate, showCancelled]);

  const counts = useMemo(() => {
    const base = { all: rows.length, arrival: 0, departure: 0, tour: 0, transfer: 0, pax: 0 };
    rows.forEach((row) => {
      base[row.service.serviceType] += 1;
      base.pax += Number(row.service.pax || 0);
    });
    return base;
  }, [rows]);

  const filtered =
    activeTab === 'all' ? rows : rows.filter((row) => row.service.serviceType === activeTab);

  const tabs: Array<{ id: DailyTab; label: string; count: number }> = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'arrival', label: 'Arrivals', count: counts.arrival },
    { id: 'departure', label: 'Departures', count: counts.departure },
    { id: 'tour', label: 'Tours', count: counts.tour },
    { id: 'transfer', label: 'Transfers', count: counts.transfer },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Operations" breadcrumbItem="Daily Operations" />

        <Card>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div>
                <h5 className="mb-1">Daily Operations</h5>
                <p className="text-muted mb-0">
                  Select a date to see every manually added service scheduled for that day.
                </p>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={() => setSelectedDate(shiftInputDate(selectedDate, -1))}
                >
                  ‹
                </Button>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{ width: 160 }}
                />
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={() => setSelectedDate(shiftInputDate(selectedDate, 1))}
                >
                  ›
                </Button>
                <Button color="primary" outline size="sm" onClick={() => setSelectedDate(toInputDateValue())}>
                  Today
                </Button>
                <Button color="secondary" outline size="sm" onClick={() => refetch()}>
                  Refresh
                </Button>
              </div>
            </div>

            <Row className="g-2 mb-3">
              <Col xs={6} md={2}>
                <Card className="border mb-0">
                  <CardBody className="py-2 px-3">
                    <div className="text-muted small">Arrivals</div>
                    <div className="fs-5 fw-semibold">{counts.arrival}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col xs={6} md={2}>
                <Card className="border mb-0">
                  <CardBody className="py-2 px-3">
                    <div className="text-muted small">Departures</div>
                    <div className="fs-5 fw-semibold">{counts.departure}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col xs={6} md={2}>
                <Card className="border mb-0">
                  <CardBody className="py-2 px-3">
                    <div className="text-muted small">Tours</div>
                    <div className="fs-5 fw-semibold">{counts.tour}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col xs={6} md={2}>
                <Card className="border mb-0">
                  <CardBody className="py-2 px-3">
                    <div className="text-muted small">Transfers</div>
                    <div className="fs-5 fw-semibold">{counts.transfer}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card className="border mb-0">
                  <CardBody className="py-2 px-3">
                    <div className="text-muted small">Total pax (listed)</div>
                    <div className="fs-5 fw-semibold">{counts.pax}</div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <Nav tabs className="border-0">
                {tabs.map((tab) => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={activeTab === tab.id ? 'active' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label} ({tab.count})
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
              <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                <Input
                  type="checkbox"
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                />
                Show cancelled
              </label>
            </div>

            <p className="text-muted small mb-3">
              Showing services for <strong>{formatOpsServiceDate(selectedDate)}</strong>
            </p>

            {error ? (
              <Alert color="danger">
                {(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]
                  ?.message || error.message}
              </Alert>
            ) : null}

            {loading && !orders.length ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading operations...</p>
              </div>
            ) : filtered.length === 0 ? (
              <Alert color="info" className="mb-0">
                No services for this date. Open a booking and use <strong>Add service</strong> on
                the itinerary section.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table className="table-bordered table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Type</th>
                      <th>Booking No</th>
                      <th>Name</th>
                      <th>Service</th>
                      <th>Code</th>
                      <th>Agency</th>
                      <th>Nationality</th>
                      <th>Hotel</th>
                      <th>Pax</th>
                      <th>INC</th>
                      <th>OPT</th>
                      <th>Flight</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Flight time</th>
                      <th>Pickup</th>
                      <th>Time</th>
                      <th>Guide</th>
                      <th>Car</th>
                      <th>Driver</th>
                      <th>Coord</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => {
                      const { service, order } = row;
                      const isTour = service.serviceType === 'tour';
                      const isArrDep =
                        service.serviceType === 'arrival' ||
                        service.serviceType === 'departure';
                      return (
                        <tr key={`${order.databaseId}-${service.id}`}>
                          <td>
                            <Badge color="info">{getOpsServiceTypeLabel(service.serviceType)}</Badge>
                          </td>
                          <td className="fw-medium">{row.orderNo}</td>
                          <td>{row.customerName}</td>
                          <td className="fw-medium">{service.serviceName}</td>
                          <td>{service.fileNo || '—'}</td>
                          <td>{service.agency || '—'}</td>
                          <td>{service.nationality || '—'}</td>
                          <td>{isArrDep ? service.hotel || '—' : '—'}</td>
                          <td>{service.pax ?? '—'}</td>
                          <td>{isTour ? service.includedPax ?? '—' : '—'}</td>
                          <td>{isTour ? service.optionalPax ?? '—' : '—'}</td>
                          <td>{isArrDep ? service.flightNumber || '—' : '—'}</td>
                          <td>{isArrDep ? service.from || '—' : '—'}</td>
                          <td>{isArrDep ? service.to || '—' : '—'}</td>
                          <td>{isArrDep ? service.flightTime || '—' : '—'}</td>
                          <td>{isTour ? service.pickupTime || '—' : '—'}</td>
                          <td>{service.serviceTime || '—'}</td>
                          <td>{isTour ? service.guide || '—' : '—'}</td>
                          <td>{service.vehicle || '—'}</td>
                          <td>{service.driver || '—'}</td>
                          <td>{service.coordinator || '—'}</td>
                          <td>
                            <Badge color={getOpsServiceStatusColor(service.status)}>
                              {getOpsServiceStatusLabel(service.status)}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              tag={Link}
                              to={`/bookings/${order.databaseId}`}
                            >
                              Open booking
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default DailyOperations;
